import { useAppSelector } from "../hooks/store.ts";
import { isBarberLoggedIn } from "../slices/authSlice.ts";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Role } from "../types";
import { useGetSubscriptionStatusQuery } from "../services/subscriptionService";
import { useNeedsOnboarding } from "../hooks/useCurrentUser";
import { PaymentRequiredModal } from "./Subscription/PaymentRequiredModal";
import { Spinner, Center, Box } from "@chakra-ui/react";

// Every route wrapped by PrivateOutlet is barber-only (/dashboard,
// /onboarding, /barber/*). Customer-only sessions must be bounced to
// /login rather than falling through to barber API calls that 401.
export function PrivateOutlet({}: {
    allowedRoles?: Role[];
    isStrictRole?: boolean;
    showMessage?: boolean;
}) {
    const isLogged = useAppSelector(isBarberLoggedIn);
    const location = useLocation();
    const needsOnboarding = useNeedsOnboarding();

    // Fetch subscription status
    const {
        data: subscription,
        isLoading: isSubscriptionLoading,
        error: subscriptionError,
    } = useGetSubscriptionStatusQuery(undefined, {
        skip: !isLogged,
    });

    // Not logged in - redirect to login
    if (!isLogged) {
        return <Navigate to="/login" state={{ from: location }} />;
    }

    // FE #24: new owners land in the wizard before they reach any other private
    // surface. /onboarding renders even while needsOnboarding is true so the
    // wizard itself doesn't redirect to itself.
    if (needsOnboarding && !location.pathname.startsWith('/onboarding')) {
        return <Navigate to="/onboarding" replace />;
    }

    // Loading subscription status
    if (isSubscriptionLoading) {
        return (
            <Center h="100vh">
                <Spinner size="xl" color="blue.500" borderWidth="4px" />
            </Center>
        );
    }

    // Subscription error - still allow access but log error
    if (subscriptionError) {
        console.error('Failed to load subscription status:', subscriptionError);
        // Continue to allow access - the API might be temporarily unavailable
    }

    // Check if payment is required
    if (subscription?.requiresPayment) {
        return (
            <Box>
                <PaymentRequiredModal subscription={subscription} open={true} />
            </Box>
        );
    }

    return <Outlet />;
}
